import React, { useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Box } from '@material-ui/core'
import clsx from 'clsx'
import Input from './Input'

const useStyles = makeStyles((theme) => ({
  input: {
    '&.MuiOutlinedInput-root': {
      height: '48px !important',
      width: '36px !important',
      padding: '8px',
      fontSize: '16px',
      '& .MuiOutlinedInput-input': {
        textAlign: 'center',
      },
    },
    [theme.breakpoints.up(530)]: {
      '&.MuiOutlinedInput-root': {
        width: '48px !important',
      },
    },
  },
  inputFilled: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.colors['primary-300'],
    },
  },
}))

/**
 * OTPInput component that allows individual editing, keyboard navigation, and paste handling.
 */
export default function OTPInput({
  length = 6,
  value,
  onChange,
  inputProps = {},
  autoFocus = false,
  ...boxProps
}) {
  const classes = useStyles()
  const inputsRef = useRef([])

  // Pad or trim the value
  const valueArr = useMemo(() => {
    const chars = value.split('').slice(0, length)
    while (chars.length < length) chars.push('')
    return chars
  }, [value, length])

  // Notify parent with joined value
  const updateValue = (arr) => onChange(arr.join(''))

  // Handle input changes (works on Android)
  const handleChange = (e, idx) => {
    const inputValue = e.target.value

    // Extract only the last digit entered
    const digit = inputValue.replace(/\D/g, '').slice(-1)

    if (digit) {
      const newArr = [...valueArr]
      newArr[idx] = digit
      updateValue(newArr)

      // Auto-focus next input
      if (idx < length - 1) {
        inputsRef.current[idx + 1]?.focus()
      }
    }
  }

  // Handle keyboard navigation and deletion
  const handleKeyDown = (e, idx) => {
    const { key } = e

    if (key === 'Backspace') {
      e.preventDefault()
      const newArr = [...valueArr]
      if (newArr[idx]) {
        // Clear current digit
        newArr[idx] = ''
        updateValue(newArr)
      } else if (idx > 0) {
        // Move to previous and clear
        inputsRef.current[idx - 1]?.focus()
        newArr[idx - 1] = ''
        updateValue(newArr)
      }
    } else if (key === 'Delete') {
      e.preventDefault()
      const newArr = [...valueArr]
      newArr[idx] = ''
      updateValue(newArr)
    } else if (key === 'ArrowLeft' && idx > 0) {
      e.preventDefault()
      inputsRef.current[idx - 1]?.focus()
    } else if (key === 'ArrowRight' && idx < length - 1) {
      e.preventDefault()
      inputsRef.current[idx + 1]?.focus()
    } else if (/^[0-9]$/.test(key)) {
      // For desktop: prevent default and handle manually
      e.preventDefault()
      const newArr = [...valueArr]
      newArr[idx] = key
      updateValue(newArr)
      if (idx < length - 1) {
        inputsRef.current[idx + 1]?.focus()
      }
    }
  }

  // Handle pasting full OTP
  const handlePaste = (e) => {
    e.preventDefault()
    const paste = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, length)
    const arr = Array.from({ length }, (_, i) => paste[i] || '')
    updateValue(arr)
    // focus last filled or last
    const focusIdx = Math.min(paste.length, length - 1)
    inputsRef.current[focusIdx]?.focus()
  }

  // Auto focus first input
  useEffect(() => {
    if (autoFocus) inputsRef.current[0]?.focus()
  }, [autoFocus])

  const sharedProps = {
    error: inputProps.error,
    ...inputProps,
  }

  return (
    <Box display="flex" gap={1} {...boxProps}>
      {valueArr.map((digit, idx) => (
        <Input
          key={idx}
          value={digit}
          onChange={(e) => handleChange(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          onFocus={(e) => e.target.select()}
          onPaste={handlePaste}
          inputRef={(el) => {
            inputsRef.current[idx] = el
          }}
          inputProps={{
            'aria-label': `OTP digit ${idx + 1}`,
            maxLength: 1,
            inputMode: 'numeric', // Show numeric keyboard on mobile
            pattern: '[0-9]*', // iOS numeric keyboard
          }}
          className={clsx(
            classes.input,
            { [classes.inputFilled]: digit !== '' },
            sharedProps.className,
          )}
          {...sharedProps}
        />
      ))}
    </Box>
  )
}

OTPInput.propTypes = {
  length: PropTypes.number,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  inputProps: PropTypes.object,
  autoFocus: PropTypes.bool,
}
